import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueries, useQueryClient } from 'react-query';
import DOMPurify from 'dompurify';
import dayjs from 'dayjs';

import { useDetailQeury, useListQeury } from '../hook';

export default function PhotoModal() {
  const listQuery = useListQeury();
  const detailQuery = useDetailQeury();

  const queryClient = useQueryClient();
  const location = useLocation();
  const history = useHistory();
  const search = new URLSearchParams(location.search);

  const photoId = search.get('photoId') ?? null;
  const slideIndex = search.get('slideIndex') ?? 1;

  const modalRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [{ data: photoList, isSuccess: listSuccess }, { data: itemDetail, isSuccess: detailSuccess }] = useQueries([
    listQuery(),
    detailQuery(),
  ]);

  const handleModalClose = () => {
    document.body.style.overflow = 'auto';
    search.delete('photoId');
    search.delete('slideIndex');
    history.push({
      search: search.toString(),
    });
  };

  const moveToPrevious = async () => {
    const prevIndex = +slideIndex - 1;

    if (prevIndex >= 1) {
      search.set('slideIndex', prevIndex);
      history.push({
        search: search.toString(),
      });
      return;
    }

    const prevItem = photoList.items[currentIndex - 1];

    if (!prevItem && photoList.currentPage > 1) {
      const list = await queryClient.fetchQuery(listQuery(photoList.currentPage - 1));

      const detail = await queryClient.fetchQuery(detailQuery(list.items[list.items.length - 1].photoId));

      search.set('photoId', detail.board_item_id);
      search.set('slideIndex', detail.image.length);
      search.set('page', photoList.currentPage - 1);
      history.push({
        search: search.toString(),
      });
      return;
    }
    if (prevItem) {
      const detail = await queryClient.fetchQuery(detailQuery(prevItem.photoId));

      search.set('photoId', prevItem.photoId);
      search.set('slideIndex', detail.image.length);
      history.push({
        search: search.toString(),
      });
      return;
    }
  };

  const moveToNext = async () => {
    const nextIndex = +slideIndex + 1;

    if (nextIndex <= itemDetail?.image?.length) {
      search.set('slideIndex', nextIndex);
      history.push({
        search: search.toString(),
      });
      return;
    }

    const nextItem = photoList.items[currentIndex + 1];

    if (!nextItem && photoList.currentPage <= photoList.totalPages) {
      const list = await queryClient.fetchQuery(listQuery(photoList.currentPage + 1));

      const detail = await queryClient.fetchQuery(detailQuery(list.items[0].photoId));

      search.set('photoId', detail.board_item_id);
      search.delete('slideIndex');
      search.set('page', photoList.currentPage + 1);
      history.push({
        search: search.toString(),
      });
      return;
    }
    if (nextItem) {
      search.set('photoId', nextItem.photoId);
      search.delete('slideIndex');
      history.push({
        search: search.toString(),
      });
      return;
    }
  };

  useEffect(() => {
    if (photoList) {
      const _currentIndex = photoList.items.findIndex((item) => item.photoId === +photoId);
      setCurrentIndex(_currentIndex);
    }
  }, [photoList, photoId]);

  useEffect(() => {
    modalRef.current?.showModal();
    document.body.style.overflow = 'hidden';
  }, []);

  return (
    <dialog ref={modalRef} className="photo-modal" onClose={handleModalClose}>
      {detailSuccess && listSuccess && (
        <form method="dialog" className="photo-modal__form" tabIndex={-1}>
          <button className="photo-modal__button photo-modal__button--close">
            <span className="sr-only">Close modal</span>
          </button>
          <div className="photo-modal__image-container">
            <button
              type="button"
              className="photo-modal__button photo-modal__button--prev"
              disabled={slideIndex <= 1 && photoList?.currentPage <= 1 && currentIndex === 0}
              onClick={moveToPrevious}
            >
              <span className="sr-only">Previous</span>
            </button>
            <img
              src={itemDetail?.image?.[slideIndex - 1]?.url}
              alt={itemDetail?.title?.[0]?.text ?? '갤러리'}
              className="photo-modal__image"
            />
            <button
              type="button"
              className="photo-modal__button photo-modal__button--next"
              disabled={
                slideIndex >= itemDetail?.image?.length &&
                photoList?.currentPage >= photoList?.totalPages &&
                currentIndex === photoList?.items.length - 1
              }
              onClick={moveToNext}
            >
              <span className="sr-only">next</span>
            </button>
          </div>
          <div className="photo-modal__info">
            <h3 className="photo-modal__title">{itemDetail?.title?.[0]?.text}</h3>
            <p
              className="photo-modal__description"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(itemDetail?.content?.[0]?.text) }}
            />
            <time className="photo-modal__date time-fs-fixed">
              {dayjs.tz(itemDetail?.reg_date).format('YYYY-MM-DD')}
            </time>
            <span className="photo-modal__counter">{`${slideIndex} / ${itemDetail?.image?.length}`}</span>
          </div>
        </form>
      )}
    </dialog>
  );
}
